import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router';
import { Icon, type IconName } from '../../components/Icon.tsx';
import { Shell } from '../../components/Shell.tsx';
import { t, type MessageKey } from '../../i18n/index.ts';
import { action, records } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { useUi } from '../../lib/ui-state.tsx';

export const SECTIONS: { to: string; icon: IconName; label: MessageKey; permission: string }[] = [
  { to: '/manage/menu', icon: 'receipt', label: 'manage.menu', permission: 'products.manage' },
  { to: '/manage/options', icon: 'list', label: 'manage.options', permission: 'products.manage' },
  { to: '/manage/happy-hour', icon: 'clock', label: 'manage.price_times', permission: 'products.manage' },
  { to: '/manage/tables', icon: 'table', label: 'manage.tables', permission: 'restaurant.configure' },
  { to: '/manage/setup', icon: 'chef', label: 'manage.setup', permission: 'restaurant.configure' },
  { to: '/manage/printers', icon: 'printer', label: 'manage.printers', permission: 'printing.manage' },
  { to: '/manage/stock', icon: 'box', label: 'manage.stock', permission: 'inventory.view' },
  { to: '/manage/warehouse', icon: 'layers', label: 'manage.warehouse', permission: 'inventory.view' },
  { to: '/manage/recipes', icon: 'book', label: 'manage.recipes', permission: 'inventory.view' },
  { to: '/manage/purchasing', icon: 'bag', label: 'manage.purchasing', permission: 'purchase.view' },
  { to: '/manage/online', icon: 'qr', label: 'manage.online', permission: 'online.manage' },
  { to: '/manage/loyalty', icon: 'gift', label: 'manage.loyalty', permission: 'loyalty.manage' },
  { to: '/manage/staff', icon: 'users', label: 'manage.staff', permission: 'staff.manage' },
  { to: '/manage/roles', icon: 'shield', label: 'manage.roles', permission: 'staff.manage' },
  { to: '/manage/team', icon: 'calendar', label: 'manage.team', permission: 'team.manage' },
  { to: '/manage/phones', icon: 'phone', label: 'manage.phones', permission: 'mobile.manage' },
  { to: '/manage/business', icon: 'note', label: 'manage.business', permission: 'company.manage' },
  { to: '/manage/locations', icon: 'store', label: 'manage.locations', permission: 'company.manage' },
  { to: '/manage/sign-in', icon: 'key', label: 'manage.sso', permission: 'company.manage' },
  { to: '/manage/taxes', icon: 'percent', label: 'manage.taxes', permission: 'company.manage' },
  { to: '/manage/currencies', icon: 'globe', label: 'manage.currencies', permission: 'company.rates' },
  { to: '/manage/fields', icon: 'sliders', label: 'manage.fields', permission: 'studio.manage' },
  { to: '/manage/approvals', icon: 'shieldCheck', label: 'manage.approvals', permission: 'studio.manage' },
  { to: '/manage/automations', icon: 'repeat', label: 'manage.automations', permission: 'studio.manage' },
  { to: '/manage/documents', icon: 'printer', label: 'manage.documents', permission: 'studio.manage' },
  { to: '/manage/import', icon: 'upload', label: 'manage.import', permission: 'data.import' },
  { to: '/manage/fiscal-journal', icon: 'lock', label: 'manage.fiscal_journal', permission: 'fiscal.journal' },
  { to: '/manage/fiscal', icon: 'shieldCheck', label: 'manage.fiscal', permission: 'fiscal.manage' },
  { to: '/manage/support', icon: 'shield', label: 'manage.support', permission: 'support.manage' },
];

/** The back office: a section menu on the left (a strip on phones) and the page on the right. */
export function ManageLayout({ title, subtitle, actions, children }: { title: string; subtitle?: string; actions?: ReactNode; children: ReactNode }) {
  const { can } = useSession();
  const nav = useRef<HTMLElement>(null);
  // On phones the menu is a strip that scrolls sideways; keep the current section in view. Only the strip
  // moves: the page itself stays at its top (on a desk the menu is a column and nothing scrolls).
  useEffect(() => {
    const box = nav.current;
    const active = box?.querySelector<HTMLElement>('.active');
    if (!box || !active || box.scrollWidth <= box.clientWidth) return;
    const a = active.getBoundingClientRect();
    const b = box.getBoundingClientRect();
    if (a.left < b.left) box.scrollLeft += a.left - b.left - 16;
    else if (a.right > b.right) box.scrollLeft += a.right - b.right + 16;
  }, []);
  return (
    <Shell>
      <div className="manage">
        <nav className="manage-nav" aria-label={t('manage.title')} ref={nav}>
          {SECTIONS.filter((s) => can(s.permission)).map((s) => (
            <NavLink key={s.to} to={s.to} className={({ isActive }) => `manage-link${isActive ? ' active' : ''}`}>
              <Icon name={s.icon} size={18} />
              <span>{t(s.label)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="page manage-page">
          <div className="page-head">
            <div>
              <h1>{title}</h1>
              {subtitle ? <p className="muted">{subtitle}</p> : null}
            </div>
            {actions ? <div className="page-actions">{actions}</div> : null}
          </div>
          {children}
        </div>
      </div>
    </Shell>
  );
}

/** Loads a list of records and reloads it on demand. */
export function useRecords<T>(model: string, query: Record<string, string> = {}) {
  const { fail } = useUi();
  const [rows, setRows] = useState<T[] | null>(null);
  const key = JSON.stringify(query);
  const reload = useCallback(
    () =>
      records.list<T>(model, JSON.parse(key)).then(setRows, (err) => {
        fail(err);
        setRows([]);
      }),
    [model, key, fail],
  );
  useEffect(() => {
    void reload();
  }, [reload]);
  return { rows, reload };
}

/** Loads an action's result once, with a reload. */
export function useAction<T>(name: string, input: Record<string, unknown> = {}) {
  const { fail } = useUi();
  const [data, setData] = useState<T | null>(null);
  const key = JSON.stringify(input);
  const reload = useCallback(() => action<T>(name, JSON.parse(key)).then(setData, fail), [name, key, fail]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data, reload };
}

/** Runs a save and reports the result; returns true when it worked. */
export function useSave() {
  const { fail, toast } = useUi();
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    try {
      await fn();
      if (done) toast(done);
      return true;
    } catch (err) {
      fail(err);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
}

export const TONES = ['blue', 'green', 'orange', 'purple', 'red', 'teal', 'yellow', 'grey'] as const;
